/**
 * UTM / featured product spotlighting on the collection page (PLP).
 *
 * Reads a `?utm_products=` query parameter containing a comma-separated list of product
 * handles and surfaces those products at the top of the collection grid, so shoppers
 * arriving from an ad / email / influencer link see the advertised products first — without
 * a dedicated landing page.
 *
 *   /collections/<handle>?utm_products=handle-1,handle-2,handle-3
 *
 * - If a product is already rendered in the grid, its existing (already hydrated) card is
 *   moved to the front — no fetch.
 * - If a product is not currently in the grid, its card is fetched by handle via the Section
 *   Rendering API (`/products/<handle>?section_id=featured-product-card`) and injected.
 *   The card is built from web components that auto-upgrade on insertion, so no manual
 *   hydration is required.
 *
 * Supports 1–4 products. Applies on initial landing only. A MutationObserver removes any
 * duplicate of a spotlighted product that later loads via infinite scroll; once the shopper
 * filters or sorts (a `filter:update` event), spotlighting stands down and normal behaviour
 * takes over.
 */

const PARAM = 'utm_products';
const MAX_PRODUCTS = 4;
const SECTION_ID = 'featured-product-card';
const FEATURED_CLASS = 'product-grid__item--featured';
const FILTER_UPDATE_EVENT = 'filter:update';

let applied = false;

/**
 * Parse, trim, lowercase, de-duplicate and cap the handles in the `utm_products` param.
 * @returns {string[]}
 */
function getHandles() {
  const raw = new URLSearchParams(window.location.search).get(PARAM);
  if (!raw) return [];

  const seen = new Set();
  const handles = [];

  for (const part of raw.split(',')) {
    const handle = part.trim().toLowerCase();
    if (!handle || seen.has(handle)) continue;
    seen.add(handle);
    handles.push(handle);
  }

  if (handles.length > MAX_PRODUCTS) {
    console.info(
      `[featured-products] ${handles.length} handles provided; only the first ${MAX_PRODUCTS} will be spotlighted.`
    );
  }

  return handles.slice(0, MAX_PRODUCTS);
}

/** @returns {string} The shop's route root, e.g. "/" or "/en-gb/". */
function getRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeAttr(value) {
  return window.CSS && CSS.escape ? CSS.escape(value) : value;
}

/**
 * @param {string} handle
 * @returns {string}
 */
function itemSelector(handle) {
  return `.product-grid__item[data-product-handle="${escapeAttr(handle)}"]`;
}

/** @returns {HTMLUListElement | null} */
function findGrid() {
  return /** @type {HTMLUListElement | null} */ (
    document.querySelector('ul.product-grid[ref="grid"]') || document.querySelector('ul.product-grid')
  );
}

/**
 * Flag a grid item as spotlighted.
 * @param {HTMLElement} item
 * @param {string} handle
 */
function markFeatured(item, handle) {
  item.classList.add(FEATURED_CLASS);
  item.dataset.featured = 'true';
  if (!item.dataset.productHandle) item.dataset.productHandle = handle;
}

/**
 * Fetch a product's card by handle and wrap it in a grid `<li>`.
 * @param {string} handle
 * @returns {Promise<HTMLLIElement | null>}
 */
async function fetchFeaturedItem(handle) {
  const url = `${getRoot()}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;

  try {
    const response = await fetch(url);
    if (!response.ok) return null; // unknown/invalid handle -> 404

    const html = await response.text();
    const card = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('product-card-link, product-card');
    if (!card) return null;

    const item = document.createElement('li');
    item.className = `product-grid__item ${FEATURED_CLASS}`;
    item.dataset.featured = 'true';
    item.dataset.productHandle = handle;

    const productId = card.getAttribute('data-product-id');
    if (productId) item.dataset.productId = productId;

    item.appendChild(card);
    return item;
  } catch (error) {
    return null;
  }
}

/**
 * Best-effort re-scan for star-rating widgets on the newly injected cards.
 */
function refreshReviewWidgets() {
  if (!document.querySelector('.yotpo-widget-instance')) return;

  const yotpo = /** @type {any} */ (window).yotpo;
  if (yotpo && typeof yotpo.refreshWidgets === 'function') {
    yotpo.refreshWidgets();
  } else if (yotpo && typeof yotpo.initWidgets === 'function') {
    yotpo.initWidgets();
  }
}

/**
 * After spotlighting, a featured product may still load again lower down via infinite scroll.
 * Remove any such duplicate (keeping the spotlighted copy) until the shopper filters/sorts.
 * @param {HTMLElement} grid
 * @param {Set<string>} handles
 */
function guardAgainstDuplicates(grid, handles) {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        if (!node.classList.contains('product-grid__item')) continue;
        if (node.dataset.featured === 'true') continue;

        const handle = node.dataset.productHandle;
        if (handle && handles.has(handle)) node.remove();
      }
    }
  });

  observer.observe(grid, { childList: true });

  document.addEventListener(
    FILTER_UPDATE_EVENT,
    () => observer.disconnect(),
    { once: true }
  );
}

async function apply() {
  if (applied) return;

  // Don't fight the back/forward scroll-restore flow.
  if (document.documentElement.classList.contains('plp-restore-pending')) return;

  const handles = getHandles();
  if (!handles.length) return;

  const grid = findGrid();
  if (!grid) return;

  applied = true;

  const items = await Promise.all(
    handles.map((handle) => {
      const existing = /** @type {HTMLElement | null} */ (grid.querySelector(itemSelector(handle)));
      if (existing) {
        markFeatured(existing, handle);
        return existing;
      }
      return fetchFeaturedItem(handle);
    })
  );

  // Build the spotlight run in the requested order. Appending an already-attached node to the
  // fragment detaches it from its current position, so existing cards are effectively moved.
  const fragment = document.createDocumentFragment();
  for (const item of items) {
    if (item) fragment.appendChild(item);
  }
  if (!fragment.childNodes.length) return;

  grid.prepend(fragment);

  // Keep only the front-most card per handle (guards against a handle listed twice, or an
  // already-present product also being fetched in a race).
  for (const handle of handles) {
    const matches = grid.querySelectorAll(itemSelector(handle));
    for (let i = 1; i < matches.length; i++) matches[i].remove();
  }

  guardAgainstDuplicates(grid, new Set(handles));
  refreshReviewWidgets();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', apply, { once: true });
} else {
  apply();
}
